import React from "react";
import { Stack, Typography } from "@mui/material";
import { t } from "i18next";
import { Cue } from "../../shared/interfaces/GameLogicTypes";
import { renderCueContent, stripCueMarkup } from "../../shared/helper/renderCueContent";
import "./HerdChoicePanel.css";

export interface HerdChoicePanelProps {
  options: Cue[];
  selectedChoice: number | null;
  onSelect: (choice: number) => void;
  isAlive: boolean;
  isMobile?: boolean;
  timeLeft: number;
}

function getFontSize(text: string): string {
  const len = typeof text === "string" ? stripCueMarkup(text).length : 0;
  if (len > 80) return "0.75rem";
  if (len > 40) return "0.85rem";
  return "1rem";
}

const HERD_COLORS = ["#608ad6", "#e75151", "#e8a838", "#7dc462"];

const HerdChoicePanel = React.memo(function HerdChoicePanel({
  options,
  selectedChoice,
  onSelect,
  isAlive,
  isMobile,
  timeLeft,
}: HerdChoicePanelProps) {
  const gridClass =
    options.length <= 2
      ? "herd-choice-grid herd-grid-2"
      : options.length === 3
      ? "herd-choice-grid herd-grid-3"
      : "herd-choice-grid herd-grid-4";

  return (
    <Stack spacing={2} alignItems="center" sx={{ width: "100%" }}>
      <div className={gridClass}>
        {options.map((cue, idx) => {
          const choiceIndex = idx + 1; // 1-based
          const isSelected = selectedChoice === choiceIndex;
          const disabled = !isAlive;
          const text = typeof cue.content === "string" ? cue.content : "";

          let className = "herd-option-btn";
          if (isSelected) className += " selected";
          if (disabled) className += " disabled";

          return (
            <div
              key={idx}
              className={className}
              style={{
                backgroundColor: HERD_COLORS[idx % HERD_COLORS.length],
                fontSize: getFontSize(text),
              }}
              onClick={() => {
                if (disabled) return;
                onSelect(choiceIndex);
              }}
            >
              {/* Resolved friend names arrive wrapped in <f> tags — render, don't print. */}
              {renderCueContent(text)}
            </div>
          );
        })}
      </div>

      {!isAlive && (
        <Typography variant="body2" color="text.secondary" textAlign="center">
          {t("herd_trampled_spectating")}
        </Typography>
      )}
      {isAlive && selectedChoice === null && (
        <Typography variant="body2" color="text.secondary" textAlign="center">
          {t("herd_pick_majority")}
        </Typography>
      )}
      {isAlive && selectedChoice !== null && (
        <Typography variant="body2" color="text.secondary" textAlign="center">
          {t("herd_locked_in")}
        </Typography>
      )}
    </Stack>
  );
});

export default HerdChoicePanel;
